import {
  DIALOG_STATE,
  UPDATE_PROFILE,
  UPDATE_USER_INFO,
  UPDATE_USER_IMAGE_URL,
  UPDATE_MODELS,
  UPDATE_MODELS_AND_FAV_MODELS,
  ADD_FAV_MODEL,
  REMOVE_FAV_MODEL,
} from "../constants/actionTypes";
import api from "../api";
import axios from "axios";
import { BASE_URL } from "../config";
import { clearAuth, getUserId, isCookieAuth, isLoggedIn, persistLogin } from "../auth";
import logger from "../utils/logger";

export const updateProfile = (data) => ({
  type: UPDATE_PROFILE,
  payload: data,
});

export const updateUserImageUrl = (data) => ({
  type: UPDATE_USER_IMAGE_URL,
  payload: data,
});

export const updateUserInfo = (data) => ({
  type: UPDATE_USER_INFO,
  payload: data,
});

export const handleChangeDialogState = (data) => ({
  type: DIALOG_STATE,
  payload: data,
});

export const logIn = (
  postData,
  succ = () => {},
  err = () => {},
  handleEmailNotVerified = () => {}
) => {
  return (dispatch) => {
    axios
      .post(`${BASE_URL}/auth/login`, postData, { withCredentials: isCookieAuth() })
      .then((response) => {
        const { msg, confirmed } = response.data;
        if (!confirmed && msg === "Please enter the code sent to your email address.") {
          handleEmailNotVerified(msg);
        } else {
          const {
            data: { access_token, refresh_token, user },
          } = response.data;
          // In cookie mode the backend has already set the session
          // cookie via Set-Cookie; persistLogin only stores the
          // non-sensitive userId. In bearer mode it persists the tokens.
          persistLogin({
            accessToken: access_token,
            refreshToken: refresh_token,
            userId: user.id,
          });
          dispatch(updateUserInfo(user));
          succ();
          return;
        }
      })
      .catch((error) => {
        logger.error("An error occurred:", error);
        err(error);
      });
  };
};

export const submitOtp = (postData, succ = () => {}, err = () => {}) => {
  return () => {
    axios
      .post(`${BASE_URL}/auth/verify-email`, postData)
      .then(() => {
        succ();
      })
      .catch((error) => {
        logger.error("An error occurred:", error);
        err(error);
      });
  };
};

export const logout = () => {
  return async (dispatch) => {
    // Local session state must clear regardless of whether the server-side
    // logout call succeeds — it didn't before (clearAuth/dispatch/redirect
    // were only reachable inside the try, so any failure there — an
    // expired/already-invalid session, a network blip, anything — left the
    // UI stuck "logged in" with no way out. Concretely hit in the wild by
    // Safari's cross-site cookie blocking (ITP): the session cookie was
    // never stored client-side to begin with, so /auth/logout 401s
    // ("Missing cookie") on every attempt, and logout looked completely
    // broken. The server call is still made best-effort — it's the right
    // thing to do when there is a real session to invalidate — it just
    // can't be a precondition for the client forgetting its own state.
    try {
      await api.post("/auth/logout");
    } catch (error) {
      logger.error("Server-side logout failed (clearing local session anyway):", error);
    }
    clearAuth();
    dispatch({ type: "LOGOUT" });
    window.open("/", "_self");
  };
};

export const submitProfile = (postData, succ = () => {}, err = () => {}) => {
  return async (dispatch) => {
    try {
      const userId = getUserId();
      const response = await api.patch(`/user/${userId}/profile`, postData);
      const { data } = response.data;
      dispatch(updateProfile(data));
      succ(data);
    } catch (error) {
      logger.error("An error occurred:", error);
      err(error);
    }
  };
};

export const getProfileInfo = (succ = () => {}, err = () => {}) => {
  return async (dispatch) => {
    try {
      const response = await api.get("/auth/myaccount");
      const { data } = response.data;
      dispatch(updateProfile(data));
      succ(data);

      const userImageResponse = await getFileUrl(data.user_img);
      dispatch(updateUserImageUrl(userImageResponse.data));
    } catch (error) {
      logger.error("An error occurred:", error);
      err(error);
    }
  };
};

export const getFileUrl = async (modelId = "") => {
  try {
    const response = await axios.get(`${BASE_URL}/marketplace/model-token/${modelId}`);
    return response.data;
  } catch (error) {
    logger.error("An error occurred:", error);
  }
};

export const getUserInfo = async () => {
  try {
    const userId = getUserId();

    const response = await api.get(`/user/${userId}/profile`);
    const { data } = response.data;
    return data;
  } catch (error) {
    logger.error("An error occurred:", error);
    return error;
  }
};

export const getUserProfile = async () => {
  try {
    const response = await api.get("/auth/myaccount");
    return response.data;
  } catch (error) {
    logger.error("An error occurred:", error);
    return error;
  }
};

export const uploadModalInfo = (postData, succ = () => {}, err = () => {}) => {
  return async () => {
    logger.debug("Upload modal info:", postData);
    const apiData = {
      name: postData.name,
      uploaded_file_info: postData.uploaded_file_info,
    };
    try {
      const response = await api.post("/model/", apiData);
      const { data } = response.data;
      succ(data);
    } catch (error) {
      logger.error("An error occurred:", error);
      err(error);
    }
  };
};

export const publishModel = (contentId, succ = () => {}, err = () => {}) => {
  return async () => {
    try {
      const response = await api.patch(`/model/${contentId}/publish`);
      const { data } = response.data;
      succ(data);
    } catch (error) {
      logger.error("An error occurred:", error);
      err(error);
    }
  };
};

export const updateModalInfo = (postData, contentId, succ = () => {}, err = () => {}) => {
  return async () => {
    const apiData = {
      name: postData.name,
      uploaded_file_info: postData.uploaded_file_info,
    };
    try {
      const response = await api.patch(`/model/${contentId}`, apiData);
      const { data } = response.data;
      succ(data);
    } catch (error) {
      logger.error("An error occurred:", error);
      err(error);
    }
  };
};

/* Presigned-PUT direct-to-B2 upload. B2's S3-compatible API doesn't
   implement POST Object (confirmed live — every attempt 501s
   "NotImplemented"), so unlike the old S3 flow this is a raw PUT with the
   file as the body, not a FormData POST with signed form fields — the
   backend's response shape changed to match (`{url, method, contentType}`,
   see `project/apis/models/routes.py`). */
export const getUploadToken = (apiData, modelId, succ = () => {}, err = () => {}, file) => {
  return () => {
    api
      .patch(`/model/upload-model/${modelId}`, apiData)
      .then((response) => {
        const { data } = response.data;
        axios
          .put(data.url, file, { headers: { "Content-Type": data.contentType } })
          .then((response) => {
            if (response.status === 200 || response.status === 204) {
              succ();
            }
          })
          .catch((error) => {
            err(error);
          });
      })
      .catch((error) => {
        logger.error("An error occurred:", error);
        err(error);
      });
  };
};

/* Profile-picture upload — server-proxied (multipart POST straight to our
   own API, not a presigned direct-to-B2 flow like models). Unlike model
   files, an avatar is small and needs jpeg/png + size validation before it
   ever touches storage, so the extra hop through our API is worth it (see
   the matching backend route's docstring, project/apis/users/routes.py).
   Response `data.user_img` is a storage key — resolve it via
   `getFileUrl`, same as any other stored key. */
export const uploadAvatar = (userId, file, succ = () => {}, err = () => {}) => {
  return async () => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post(`/user/${userId}/avatar`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      succ(response.data.data);
    } catch (error) {
      logger.error("Failed to upload avatar", error);
      err(error);
    }
  };
};

export const dispatchModelsInStore = (data) => ({
  type: UPDATE_MODELS,
  payload: data,
});

export const dispatchModelsAndFavModelsInStore = (data) => ({
  type: UPDATE_MODELS_AND_FAV_MODELS,
  payload: data,
});

export const getUploadedModels = async (dispatch) => {
  try {
    const userId = getUserId();
    const getModels = await api.get(`/user/${userId}/models`);
    // const favModels = await getFavModels();
    dispatch(dispatchModelsInStore(getModels?.data?.data?.model || []));
    return getModels.data;
  } catch (error) {
    logger.error("An error occurred:", error);
    return error;
  }
};

/* Explore page — knowledge-graph-backed trees (GET /graph/disciplines,
   GET /graph/biocomplexity) and single-node detail (GET /graph/node/<id>).
   All resolve to `null` on failure so the page can fall back to the
   caller renders a load-failure state. */
export const getDisciplinesTree = async () => {
  try {
    const response = await api.get("/graph/disciplines");
    const data = response?.data?.data ?? response?.data ?? null;
    return Array.isArray(data) && data.length ? data : null;
  } catch (error) {
    logger.error("Failed to load disciplines tree", error);
    return null;
  }
};

export const getBiocomplexityTree = async () => {
  try {
    const response = await api.get("/graph/biocomplexity");
    const data = response?.data?.data ?? response?.data ?? null;
    return Array.isArray(data) && data.length ? data : null;
  } catch (error) {
    logger.error("Failed to load biocomplexity tree", error);
    return null;
  }
};

/* `labels` narrows the returned neighborhood to nodes carrying those Neo4j
   labels — the Biocomplexity secondary filters (see Explore/layerSelection).
   Sent as a repeatable `labels` query param, omitted entirely when empty so
   the request is byte-identical to the unfiltered one. Explore also applies
   the same filter client-side, so an endpoint that ignores the param still
   renders the right graph. */
export const getGraphNode = async (nodeId, labels = []) => {
  try {
    const response = await api.get(`/graph/node/${encodeURIComponent(nodeId)}`, {
      params: labels?.length ? { labels } : undefined,
    });
    return response?.data?.data ?? response?.data ?? null;
  } catch (error) {
    logger.error("Failed to load graph node", error);
    return null;
  }
};

/* Bundled 3D-model catalog metadata, served from the backend (seeded from
   the same data that used to live in `data/staticModels.js`). Resolves to
   `null` on failure so the page can fall back to the bundled constant. */
export const getStaticModelCatalog = async () => {
  try {
    const response = await api.get("/model/catalog");
    const data = response?.data?.data ?? response?.data ?? null;
    return Array.isArray(data) && data.length ? data : null;
  } catch (error) {
    logger.error("Failed to load static model catalog", error);
    return null;
  }
};

export const getUploadedModelsWithoutToken = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/marketplace/homepage`);
    return response.data;
  } catch (error) {
    logger.error("An error occurred:", error);
    return error;
  }
};

export const addComment = async (postData, id, succ = () => {}, err = () => {}) => {
  try {
    const response = await api.post(`/webapp/add-comment/${id}`, postData);
    succ(response);
    return response;
  } catch (error) {
    logger.error("An error occurred:", error);
    err(error);
    return error;
  }
};

/* Comments on a knowledge-graph node — ticket 86ak29prz. Same `msg`
   response-shape convention as `addComment`, different backend route
   (graph nodes have no Mongo id, so they aren't `/webapp/add-comment/<id>`,
   see backend `project/apis/graph/routes.py`). `nodeId` is the same
   `<prefix>:<key>` string used everywhere else in Explore. */
export const getNodeComments = async (nodeId) => {
  try {
    const response = await api.get(`/graph/node/${encodeURIComponent(nodeId)}/comments`);
    return response?.data?.data ?? [];
  } catch (error) {
    logger.error("Failed to load node comments", error);
    return [];
  }
};

export const addNodeComment = async (nodeId, comment, succ = () => {}, err = () => {}) => {
  try {
    const response = await api.post(`/graph/node/${encodeURIComponent(nodeId)}/comments`, {
      comment,
    });
    succ(response);
    return response;
  } catch (error) {
    logger.error("Failed to post node comment", error);
    err(error);
    return error;
  }
};

export const addFavorite = async (postData, succ = () => {}, err = () => {}) => {
  try {
    const response = await api.post(`/model/${postData.id}/like`, {});
    succ(response);
    return response;
  } catch (error) {
    logger.error("An error occurred:", error);
    err(error.response);
    return error;
  }
};

export const removeFavorite = async (postData, succ = () => {}, err = () => {}) => {
  try {
    const response = await api.delete(`/model/${postData.id}/like`, {});
    succ(response);
    return response;
  } catch (error) {
    logger.error("An error occurred:", error);
    err(error.response.data);
    return error;
  }
};

export const getFavModels = async () => {
  try {
    const userId = getUserId();
    const response = await api.get(`/user/${userId}/liked-models`);
    return response.data;
  } catch (error) {
    logger.error("An error occurred:", error);
    return error;
  }
};

export const getSingleModels = async (modelId) => {
  try {
    const response = await api.get(`/model/${modelId}`);
    return response.data.data;
  } catch (error) {
    return error.response.data;
  }
};

export const addFavModel = (data) => ({
  type: ADD_FAV_MODEL,
  payload: data,
});

export const removeFavModel = (data) => ({
  type: REMOVE_FAV_MODEL,
  payload: data,
});

export const removeModel = async (modelId, succ = () => {}, err = () => {}) => {
  try {
    const response = await api.delete(`/model/${modelId}`, {});
    succ(response);
    return response;
  } catch (error) {
    logger.error("An error occurred:", error);
    if (error) {
      err(error?.response?.data);
    }
    return error;
  }
};

export const checkUsernameUniqueness = async (username) => {
  try {
    const response = await api.get(`/user/check-username-availability/${username}`);
    return !response?.data?.error;
  } catch (error) {
    logger.error("Error checking username uniqueness", error);
    return false;
  }
};

/* ── Science Education Standards — saved selections ─────────────────────
   The standards picker lets a user select whole standards (or, in advanced
   mode, individual components) and persist them. These wrap the standards
   saves endpoint on the authed API client. They resolve to null/[] on
   failure so the UI can fall back gracefully while the backend endpoint is
   still being built out. */

export const getSavedStandards = async () => {
  // Anonymous visitors always get a 401 here — skip the round-trip (and the
  // console noise it produces on every Standards page load) rather than
  // firing a request we already know the server will reject.
  if (!isLoggedIn()) return [];
  try {
    const response = await api.get("/standards/saved");
    const data = response?.data?.data ?? response?.data ?? [];
    return Array.isArray(data) ? data : [];
  } catch (error) {
    logger.error("Failed to load saved standards", error);
    return [];
  }
};

export const saveStandardSelection = async (payload, succ = () => {}, err = () => {}) => {
  try {
    const response = await api.post("/standards/saved", payload);
    const data = response?.data?.data ?? response?.data ?? null;
    succ(data);
    return data;
  } catch (error) {
    logger.error("Failed to save standard selection", error);
    err(error);
    return null;
  }
};

export const deleteSavedStandard = async (id, succ = () => {}, err = () => {}) => {
  try {
    await api.delete(`/standards/saved/${id}`);
    succ();
    return true;
  } catch (error) {
    logger.error("Failed to delete saved standard", error);
    err(error);
    return false;
  }
};

/* ── Alignment notes — server sync for AlignStandards/notes.js ───────────
   That module's own docstring: "Notes are kept in localStorage so they
   survive reloads today; the analysis service will own them once
   note-sync ships." (Ticket 86ak29prz.) `useAlignmentNotes` calls these
   as a best-effort background sync — localStorage stays the source of
   truth for render (instant, no loading state), the server copy is for
   cross-device continuity. Anonymous visitors skip the round-trip, same
   as `getSavedStandards`. */

export const getAlignmentNotes = async (source) => {
  if (!isLoggedIn()) return [];
  try {
    const response = await api.get("/standards/align/notes", { params: { source } });
    const data = response?.data?.data ?? [];
    return Array.isArray(data) ? data : [];
  } catch (error) {
    logger.error("Failed to load alignment notes", error);
    return [];
  }
};

export const createAlignmentNote = async (payload, succ = () => {}, err = () => {}) => {
  try {
    const response = await api.post("/standards/align/notes", payload);
    const data = response?.data?.data ?? null;
    succ(data);
    return data;
  } catch (error) {
    logger.error("Failed to sync new alignment note", error);
    err(error);
    return null;
  }
};

export const updateAlignmentNote = async (id, patch, succ = () => {}, err = () => {}) => {
  try {
    const response = await api.patch(`/standards/align/notes/${id}`, patch);
    const data = response?.data?.data ?? null;
    succ(data);
    return data;
  } catch (error) {
    logger.error("Failed to sync alignment note update", error);
    err(error);
    return null;
  }
};

export const deleteAlignmentNote = async (id, succ = () => {}, err = () => {}) => {
  try {
    await api.delete(`/standards/align/notes/${id}`);
    succ();
    return true;
  } catch (error) {
    logger.error("Failed to sync alignment note delete", error);
    err(error);
    return false;
  }
};

/* ── Saved documents (Analyze Document / Align Materials library) ────────────
   The account's "Uploads" tab lists uploaded documents alongside 3D models.
   Resolves to [] until the endpoint ships, so the tab renders gracefully with
   no documents rather than erroring. */
export const getSavedDocuments = async () => {
  try {
    const userId = getUserId();
    const response = await api.get(`/user/${userId}/documents`);
    const data = response?.data?.data?.documents ?? response?.data?.data ?? response?.data ?? [];
    return Array.isArray(data) ? data : [];
  } catch (error) {
    logger.error("Failed to load saved documents", error);
    return [];
  }
};

/* The site's light-mode background photos. Returns an array of image URLs;
   falls back to [] on failure so the app can use its bundled local set. Each
   item may be a bare URL string or an object carrying a url/src field. */
export const getBackgrounds = async () => {
  try {
    const response = await api.get("/backgrounds");
    const data = response?.data?.data?.images ?? response?.data?.data ?? response?.data ?? [];
    const list = Array.isArray(data) ? data : [];
    return list.map((x) => (typeof x === "string" ? x : x?.url || x?.src)).filter(Boolean);
  } catch (error) {
    logger.error("Failed to load backgrounds", error);
    return [];
  }
};

/* The account "Comments" tab lists comments the signed-in user has posted on
   models. Resolves to [] until the endpoint ships, so the tab renders an empty
   state rather than erroring. */
export const getUserComments = async () => {
  try {
    const userId = getUserId();
    const response = await api.get(`/user/${userId}/comments`);
    const data = response?.data?.data?.comments ?? response?.data?.data ?? response?.data ?? [];
    return Array.isArray(data) ? data : [];
  } catch (error) {
    logger.error("Failed to load user comments", error);
    return [];
  }
};

/* ── Materials → Standards alignment — microservice handoff ─────────────────
   The alignment page sends the selected standards + the material's text to the
   analysis microservice, which scores each standard's four-part model against
   the document and returns coverage + evidence.

   Request  (POST /standards/align):
     {
       source:     "ngss",                         // source registry key
       sourceName: "Next Generation Science Standards",
       docType:    "text",                          // material type
       model:      ["knowledge","skills","context","performance"],
       standards:  [{ code, statement, keywords, clarification,
                      assessmentBoundary, components }, …],  // full normalised
                                                               // standard objects
       material:   { name, text, wordCount },       // extracted / pasted text
     }

   Response — the same shape the in-browser engine (pages/AlignStandards/model
   → alignAll) produces, so the UI is agnostic to which one ran:
     {
       results: [{ code, grade, domain, topic, statement, score, status,
                   facets: { knowledge|skills|context|performance:
                             { score, matched:[…], total, derived } },
                   evidence: [{ text, terms:[…], facets:[…] }] }],
       summary: { total, averageScore, counts:{full,partial,none},
                  facetAverages:{ knowledge, skills, context, performance } },
     }

   Resolves to null on failure or until the endpoint ships, so the page can
   fall back to running the local heuristic engine in the browser. A finite
   timeout guarantees a stalled endpoint rejects (→ null → fallback) rather
   than hanging the page, since the shared axios client sets no default. */
export const alignMaterialToStandards = async (payload, succ = () => {}, err = () => {}) => {
  try {
    const response = await api.post("/standards/align", payload, { timeout: 20000 });
    const data = response?.data?.data ?? response?.data ?? null;
    succ(data);
    return data;
  } catch (error) {
    logger.error("Failed to align material to standards", error);
    err(error);
    return null;
  }
};

/* Persist a completed alignment to the user's library. Returns the saved
   record (with an id) on success, or null until the endpoint ships — the page
   uses the return value to confirm honestly (real save vs. session-only). */
export const saveAlignment = async (payload, succ = () => {}, err = () => {}) => {
  try {
    const response = await api.post("/standards/align/saved", payload);
    const data = response?.data?.data ?? response?.data ?? null;
    succ(data);
    return data;
  } catch (error) {
    logger.error("Failed to save alignment", error);
    err(error);
    return null;
  }
};

/* Real deterministic text analysis (vocabulary, phrases, sections, POS,
   readability, concept graph) — see backend `project/apis/analyze/textstats.py`.
   Resolves to null on failure so the page can fall back to its sample output. */
export const analyzeDocument = async (payload, succ = () => {}, err = () => {}) => {
  try {
    const response = await api.post("/analyze/document", payload, { timeout: 20000 });
    const data = response?.data?.data ?? response?.data ?? null;
    succ(data);
    return data;
  } catch (error) {
    logger.error("Failed to analyze document", error);
    err(error);
    return null;
  }
};

/* Persist a completed document analysis to the user's library (MyAccount
   "Uploads" tab, via GET /user/{id}/documents). */
export const saveAnalysis = async (payload, succ = () => {}, err = () => {}) => {
  try {
    const response = await api.post("/analyze/document/saved", payload);
    const data = response?.data?.data ?? response?.data ?? null;
    succ(data);
    return data;
  } catch (error) {
    logger.error("Failed to save analysis", error);
    err(error);
    return null;
  }
};
